import { z } from 'zod';
import { UserStatus, UserRole, UserInviteStatus } from './constants';
import { ControllerRenderProps } from 'react-hook-form';

// User type definition
export type User = {
  id: string;
  firstName: string;
  lastName: string;
  emailAddress: string;
  role: UserRole;
  status: UserStatus;
  invitationStatus?: UserInviteStatus;
  isCurrentUser?: boolean;
  providerTypeId?: string;
  providerType?: string;
  profileAccess?: Array<{ id: string; name: string }>;
  isSoleOwner?: boolean;
  canBeEdited?: boolean;
};

// Schema for edit user form
export const editUserSchema = z
  .object({
    firstName: z.string().min(1, { message: 'Enter a first name' }),
    lastName: z.string().min(1, { message: 'Enter a last name' }),
    emailAddress: z.email({ message: 'Enter a valid email address' }),
    providerTypeId: z.string().optional(),
    role: z.enum(UserRole),
    profileAccess: z.array(z.string()),
  })
  .refine(
    (data) => data.role === UserRole.OWNER || data.profileAccess.length > 0,
    {
      message: 'Select at least one profile',
      path: ['profileAccess'],
    },
  );

// currently duplicate of editUserSchema
export const createUserSchema = z
  .object({
    firstName: z.string().min(1, { message: 'Enter a first name' }),
    lastName: z.string().min(1, { message: 'Enter a last name' }),
    emailAddress: z.email({ message: 'Enter a valid email address' }),
    providerTypeId: z.string().optional(),
    role: z.enum(UserRole),
    organizationProfileIds: z.array(z.string()),
  })
  .refine(
    (data) =>
      data.role === UserRole.OWNER || data.organizationProfileIds.length > 0, // only the owner doesn't need to select profiles
    {
      message: 'Select at least one profile',
      path: ['organizationProfileIds'],
    },
  );

export type EditUserFormData = z.infer<typeof editUserSchema>;
export type CreateUserFormData = z.infer<typeof createUserSchema>;
// Form field props type helper
export type EditFormFieldProps<T extends keyof EditUserFormData> = {
  field: ControllerRenderProps<EditUserFormData, T>;
};

export type CreateFormFieldProps<T extends keyof CreateUserFormData> = {
  field: ControllerRenderProps<CreateUserFormData, T>;
};

export type SubmitAction = 'createAndAdd' | 'createAndClose';

// User profile types
export interface ProfileOption {
  value: string;
  label: string;
  avatarType: string;
  disabled: boolean;
}

export interface ProfileSelection {
  id: string;
  displayName: string;
  avatarType: string;
}
